"use client";

import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Mail,
  Plus,
  Trash2,
  Edit,
  X,
  AlertTriangle,
  Link as LinkIcon,
  Loader2,
  Calendar,
  UserCheck,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Info,
  RefreshCw,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAgentInboxes } from "@/hooks/agents/email/use-agent-inboxes";
import { useAllInboxes, InboxWithUsage } from "@/hooks/agents/email/use-agents";
import { useAuth } from "@/hooks/auth/use-auth";
import { getTodayInTimezone, dateToTimezoneISO } from "@/lib/date-utils";
import { SharedInboxDialog } from "../dialogs/shared-inbox-dialog";
import { useEmailAgentConfig, type EmailAgentConfig } from "@/hooks/agents/email";
import { PageLoader } from "@/components/ui/page-loader";
import { useTranslations } from "next-intl"



interface IdentityOverrideForm {
  name: string;
  source_email: string;
  name_regex: string;
  email_regex: string;
  enabled: boolean;
}

const defaultIdentityForm: IdentityOverrideForm = {
  name: "",
  source_email: "",
  name_regex: "/Name:\\s*(.+?)(?:\\n|$)/mi",
  email_regex: "/Email:\\s*([^\\s<>]+@[^\\s<>]+)/mi",
  enabled: true,
};

interface InboxSettingsProps {
  agentId: string;
  onInboxesChange?: () => void;
}

export function InboxSettings({ agentId, onInboxesChange }: InboxSettingsProps) {

  const tUi = useTranslations("Email.AgentsEmailTabsInboxSettings")
  const tCommonUi = useTranslations("Common")

  const { user } = useAuth();
  const userTimezone = user?.timezone || "UTC";

  const {
    inboxes: connectedInboxes,
    isLoading: isLoadingConnected,
    isAdding,
    isRemoving,
    error: connectedError,
    addInbox,
    removeInbox,
    connectNewInbox,
    refreshInboxes: _refreshInboxes,
  } = useAgentInboxes(agentId);

  const {
    inboxes: allInboxes,
    isLoading: isLoadingAll,
    error: allError,
    refreshInboxes: refreshAllInboxes,
  } = useAllInboxes();

  const [selectedSharedInbox, setSelectedSharedInbox] =
    useState<InboxWithUsage | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Track sync_from_date for each available inbox (default to today in user's timezone)
  const [inboxSyncDates, setInboxSyncDates] = useState<Record<string, string>>({});

  // Identity override config
  const {
    config,
    isLoading: isLoadingConfig,
    isSaving: isSavingConfig,
    updateIdentityOverrides,
    loadConfig,
  } = useEmailAgentConfig(agentId);

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Identity override state
  const [showIdentityForm, setShowIdentityForm] = useState(false);
  const [identityForm, setIdentityForm] = useState<IdentityOverrideForm>(defaultIdentityForm);
  const [editingIdentityIndex, setEditingIdentityIndex] = useState<number | null>(null);
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);

  // Local state for pending identity override changes
  const [localOverrides, setLocalOverrides] = useState<NonNullable<EmailAgentConfig["identity_overrides"]> | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Get sync date for an inbox (for display in date input), defaulting to today in user's timezone
  const getSyncDate = (inboxId: string) => {
    return inboxSyncDates[inboxId] || getTodayInTimezone(userTimezone);
  };

  // Get sync date as ISO timestamp (for API), respecting user's timezone
  const getSyncDateISO = (inboxId: string) => {
    const dateStr = getSyncDate(inboxId);
    return dateToTimezoneISO(dateStr, userTimezone);
  };

  const setSyncDate = (inboxId: string, date: string) => {
    setInboxSyncDates((prev) => ({ ...prev, [inboxId]: date }));
  };

  // Filter out already connected inboxes from available list
  const connectedIds = new Set(connectedInboxes.map((i) => i.id));
  const availableInboxes = allInboxes.filter((i) => !connectedIds.has(i.id));

  const handleAddInbox = async (inbox: InboxWithUsage) => {
    setActionError(null);

    // Check if inbox is used by other agents
    const otherAgents = inbox.used_by_agents.filter(
      (a) => a.id !== agentId
    );

    if (otherAgents.length > 0) {
      // Show confirmation dialog
      setSelectedSharedInbox(inbox);
      return;
    }

    // Add directly if not shared
    try {
      const syncDate = getSyncDateISO(inbox.id);
      await addInbox(inbox.id, syncDate);
      refreshAllInboxes();
      onInboxesChange?.();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to add inbox");
    }
  };

  const handleConfirmSharedInbox = async () => {
    if (!selectedSharedInbox) return;

    try {
      const syncDate = getSyncDateISO(selectedSharedInbox.id);
      await addInbox(selectedSharedInbox.id, syncDate);
      refreshAllInboxes();
      setSelectedSharedInbox(null);
      onInboxesChange?.();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to add inbox");
    }
  };

  const handleRemoveInbox = async (inboxId: string) => {
    setActionError(null);

    try {
      await removeInbox(inboxId);
      refreshAllInboxes();
      onInboxesChange?.();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to remove inbox");
    }
  };

  // Identity override handlers
  // Use local state if there are unsaved changes, otherwise use config
  const identityOverrides = localOverrides ?? config.identity_overrides ?? [];

  const handleAddOrUpdateIdentity = () => {
    // email_regex is REQUIRED for identity extraction to work
    if (!identityForm.name || !identityForm.source_email || !identityForm.email_regex) return;

    const currentOverrides = [...identityOverrides];

    const newOverride = {
      name: identityForm.name,
      source_email: identityForm.source_email,
      name_regex: identityForm.name_regex || undefined,
      email_regex: identityForm.email_regex,
      enabled: identityForm.enabled,
    };

    if (editingIdentityIndex !== null) {
      currentOverrides[editingIdentityIndex] = newOverride;
    } else {
      currentOverrides.push(newOverride);
    }

    setLocalOverrides(currentOverrides);
    setHasUnsavedChanges(true);
    setShowIdentityForm(false);
    setIdentityForm(defaultIdentityForm);
    setEditingIdentityIndex(null);
  };

  const handleEditIdentity = (index: number) => {
    const override = identityOverrides[index];
    if (override) {
      setIdentityForm({
        name: override.name,
        source_email: override.source_email,
        name_regex: override.name_regex || "",
        email_regex: override.email_regex,
        enabled: override.enabled,
      });
      setEditingIdentityIndex(index);
      setShowIdentityForm(true);
    }
  };

  const handleDeleteIdentity = (index: number) => {
    const newOverrides = identityOverrides.filter((_, i) => i !== index);
    setLocalOverrides(newOverrides);
    setHasUnsavedChanges(true);
  };

  const handleCancelIdentity = () => {
    setShowIdentityForm(false);
    setIdentityForm(defaultIdentityForm);
    setEditingIdentityIndex(null);
  };

  const handleSaveIdentitySettings = async () => {
    if (!hasUnsavedChanges || localOverrides === null) return;

    try {
      await updateIdentityOverrides(localOverrides);
      setHasUnsavedChanges(false);
      setLocalOverrides(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to save identity settings");
    }
  };

  const handleDiscardIdentityChanges = () => {
    setLocalOverrides(null);
    setHasUnsavedChanges(false);
    setShowIdentityForm(false);
    setIdentityForm(defaultIdentityForm);
    setEditingIdentityIndex(null);
  };

  const isLoading = isLoadingConnected || isLoadingAll;
  const error = connectedError || allError || actionError;

  const getProviderBadge = (provider: string) => {
    switch (provider) {
      case "gmail":
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
            <span key={"Gmail"}>{"Gmail"}</span></Badge>
        );
      case "outlook":
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            <span key={"Outlook"}>{"Outlook"}</span></Badge>
        );
      default:
        return (
          <Badge variant="outline">
            {provider}
          </Badge>
        );
    }
  };

  // Show full-page loader while initial data is loading
  if (isLoading) {
    return <PageLoader message={tUi("loadingInboxes")} />;
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Connected Inboxes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            <span><span key={tUi("connectedInboxes")}>{tUi("connectedInboxes")}</span></span>
          </CardTitle>
          <CardDescription>
            <span key={tUi("emailAccountsThatThisAgentMonitorsAndResponds")}>{tUi("emailAccountsThatThisAgentMonitorsAndResponds")}</span></CardDescription>
        </CardHeader>
        <CardContent>
          {connectedInboxes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p><span key={tUi("noInboxesConnectedYet")}>{tUi("noInboxesConnectedYet")}</span></p>
              <p className="text-sm mt-1">
                <span key={tUi("addAnInboxBelowOrConnectANew")}>{tUi("addAnInboxBelowOrConnectANew")}</span></p>
            </div>
          ) : (
            <div className="space-y-2">
              {connectedInboxes.map((inbox) => {
                // Check oauth status from allInboxes (which has the full data)
                const fullInbox = allInboxes.find(i => i.id === inbox.id);
                const oauthStatus = fullInbox?.oauth_status || 'connected';
                const hasOAuthError = oauthStatus !== 'connected';

                return (
                  <div
                    key={inbox.id}
                    className={`p-3 border rounded-md ${hasOAuthError ? 'bg-red-50 border-red-200' : 'bg-white'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Mail className={`w-4 h-4 ${hasOAuthError ? 'text-red-500' : 'text-gray-500'}`} />
                        <div>
                          <div className="font-medium">{inbox.address}</div>
                        </div>
                        {getProviderBadge(inbox.provider)}
                        {hasOAuthError && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            <span key={oauthStatus === 'token_expired' ? tUi("tokenExpired") : tUi("error")}>{oauthStatus === 'token_expired' ? tUi("tokenExpired") : tUi("error")}</span>
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {hasOAuthError && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={connectNewInbox}
                                className="text-red-600 border-red-300 hover:bg-red-100"
                              >
                                <RefreshCw className="h-4 w-4 mr-1" />
                                <span><span key={tUi("reconnect")}>{tUi("reconnect")}</span></span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p><span key={tUi("reconnectGoogleAccountToFixAuthorization")}>{tUi("reconnectGoogleAccountToFixAuthorization")}</span></p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveInbox(inbox.id)}
                          disabled={isRemoving === inbox.id}
                        >
                          {isRemoving === inbox.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4 text-red-500" />
                          )}
                        </Button>
                      </div>
                    </div>
                    {hasOAuthError && fullInbox?.last_error && (
                      <div className="mt-2 text-xs text-red-600 bg-red-100 p-2 rounded">
                        {fullInbox.last_error}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Inboxes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            <span><span key={tUi("availableInboxes")}>{tUi("availableInboxes")}</span></span>
          </CardTitle>
          <CardDescription>
            <span key={tUi("otherEmailAccountsFromYourIntegrationsThatCan")}>{tUi("otherEmailAccountsFromYourIntegrationsThatCan")}</span></CardDescription>
        </CardHeader>
        <CardContent>
          {availableInboxes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p><span key={tUi("noAdditionalInboxesAvailable")}>{tUi("noAdditionalInboxesAvailable")}</span></p>
              <p className="text-sm mt-1">
                <span key={tUi("connectANewGoogleAccountToAddMore")}>{tUi("connectANewGoogleAccountToAddMore")}</span></p>
            </div>
          ) : (
            <div className="space-y-3">
              {availableInboxes.map((inbox) => {
                const isUsedByOthers = inbox.used_by_agents.length > 0;
                return (
                  <div
                    key={inbox.id}
                    className="p-3 border rounded-md bg-white space-y-2"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-3 flex-1">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium flex flex-wrap items-center gap-2">
                            <span className="break-all">{inbox.address}</span>
                            {isUsedByOthers && (
                              <Badge
                                variant="outline"
                                className="bg-yellow-50 text-yellow-700 border-yellow-200"
                              >
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                <span key={tUi("usedByAgents", { count: inbox.used_by_agents.length })}>{tUi("usedByAgents", { count: inbox.used_by_agents.length })}</span>
                              </Badge>
                            )}
                          </div>
                          {isUsedByOthers && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {inbox.used_by_agents.map((a) => a.name).join(", ")}
                            </div>
                          )}
                        </div>
                        {getProviderBadge(inbox.provider)}
                      </div>
                      <Button
                        className="shrink-0"
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddInbox(inbox)}
                        disabled={isAdding}
                      >
                        {isAdding ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Plus className="h-4 w-4 mr-2" />
                        )}
                        <span><span key={tUi("add")}>{tUi("add")}</span></span>
                      </Button>
                    </div>
                    {/* Sync from date picker */}
                    <div className="flex flex-wrap items-center gap-2 text-sm sm:ml-7">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <Label htmlFor={`sync-date-${inbox.id}`} className="text-xs text-gray-600">
                        <span key={tUi("syncFrom")}>{tUi("syncFrom")}</span></Label>
                      <Input
                        id={`sync-date-${inbox.id}`}
                        type="date"
                        value={getSyncDate(inbox.id)}
                        onChange={(e) => setSyncDate(inbox.id, e.target.value)}
                        className="h-8 w-[140px] text-xs"
                      />
                      <span className="text-xs text-muted-foreground">
                        <span key={tUi("syncWillNotFetchEmailsBeforeThisDate")}>{tUi("syncWillNotFetchEmailsBeforeThisDate")}</span></span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-4 pt-4 border-t">
            <Button onClick={connectNewInbox} className="w-full">
              <LinkIcon className="w-4 h-4 mr-2" />
              <span><span key={tUi("connectNewGoogleAccount")}>{tUi("connectNewGoogleAccount")}</span></span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Shared Inbox Confirmation Dialog */}
      <SharedInboxDialog
        open={!!selectedSharedInbox}
        onOpenChange={(open) => !open && setSelectedSharedInbox(null)}
        inbox={selectedSharedInbox}
        currentAgentId={agentId}
        onConfirm={handleConfirmSharedInbox}
        isLoading={isAdding}
      />

      {/* Identity Overrides */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="w-5 h-5" />
            <span><span key={tUi("identityOverrides")}>{tUi("identityOverrides")}</span></span>
          </CardTitle>
          <CardDescription>
            <span key={tUi("configureRulesToExtractSenderIdentityFromEmail")}>{tUi("configureRulesToExtractSenderIdentityFromEmail")}</span></CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingConfig ? (
            <PageLoader message={tUi("loadingIdentitySettings")} size="sm" className="h-32" />
          ) : (
            <>
              <div className="flex items-center justify-end">
                <Button
                  size="sm"
                  onClick={() => setShowIdentityForm(true)}
                  className="h-8 px-3 text-xs"
                  disabled={showIdentityForm}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  <span><span key={tUi("addOverride")}>{tUi("addOverride")}</span></span>
                </Button>
              </div>

              {/* Existing overrides list */}
              {identityOverrides.length === 0 && !showIdentityForm ? (
                <div className="p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <div className="text-center">
                    <UserCheck className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 mb-2"><span key={tUi("noIdentityOverrideSettingsConfigured")}>{tUi("noIdentityOverrideSettingsConfigured")}</span></p>
                    <p className="text-xs text-gray-500"><span key={tUi("configureRulesToExtractIdentitiesFromSpecificEmail")}>{tUi("configureRulesToExtractIdentitiesFromSpecificEmail")}</span></p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {identityOverrides.map((override, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-medium text-gray-900">{override.name}</h4>
                          <Badge variant={override.enabled ? "default" : "secondary"} className="text-xs">
                            <span key={override.enabled ? tUi("enabled") : tUi("disabled")}>{override.enabled ? tUi("enabled") : tUi("disabled")}</span>
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditIdentity(index)}
                            className="h-6 w-6 p-0"
                            disabled={isSavingConfig}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteIdentity(index)}
                            className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            disabled={isSavingConfig}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-1 text-xs text-gray-600">
                        <div>
                          <span className="font-medium"><span key={tUi("source")}>{tUi("source")}</span></span> {override.source_email}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Identity Override Form */}
              {showIdentityForm && (
                <div className="border-t pt-4 mt-4">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 key={editingIdentityIndex !== null ? tUi("editIdentityOverride") : tUi("addIdentityOverride")} className="text-sm font-medium text-gray-900">
                        {editingIdentityIndex !== null ? tUi("editIdentityOverride") : tUi("addIdentityOverride")}
                      </h4>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleCancelIdentity}
                        className="h-6 w-6 p-0"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>

                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          <span><span key={tUi("name")}>{tUi("name")}</span></span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="w-4 h-4 inline ml-1 text-gray-400 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p><span key={tUi("aDescriptiveNameForThisIdentityOverrideRule")}>{tUi("aDescriptiveNameForThisIdentityOverrideRule")}</span></p>
                            </TooltipContent>
                          </Tooltip>
                        </Label>
                        <Input
                          placeholder={tUi("eGContactFormSubmissions")}
                          value={identityForm.name}
                          onChange={(e) => setIdentityForm((prev) => ({ ...prev, name: e.target.value }))}
                          className="text-sm"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          <span><span key={tUi("sourceEmailAddress")}>{tUi("sourceEmailAddress")}</span></span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="w-4 h-4 inline ml-1 text-gray-400 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p><span key={tUi("whenEmailsAreReceivedFromThisAddressExtract")}>{tUi("whenEmailsAreReceivedFromThisAddressExtract")}</span></p>
                            </TooltipContent>
                          </Tooltip>
                        </Label>
                        <Input
                          type="email"
                          placeholder={"notifications@example.com"}
                          value={identityForm.source_email}
                          onChange={(e) => setIdentityForm((prev) => ({ ...prev, source_email: e.target.value }))}
                          className="text-sm"
                        />
                      </div>

                      <Collapsible open={advancedExpanded} onOpenChange={setAdvancedExpanded}>
                        <CollapsibleTrigger asChild>
                          <Button
                            variant="ghost"
                            type="button"
                            className="flex items-center justify-between w-full p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100"
                          >
                            <span className="text-sm font-medium text-gray-700"><span key={tUi("advanced")}>{tUi("advanced")}</span></span>
                            {advancedExpanded ? (
                              <ChevronUp className="w-4 h-4 text-gray-500" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-500" />
                            )}
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="p-3 bg-gray-50 border-l border-r border-b border-gray-200 rounded-b-lg -mt-1 space-y-3">
                            <p className="text-xs text-gray-500">
                              <span key={tUi("regexPatternsToExtractNameAndEmailFrom")}>{tUi("regexPatternsToExtractNameAndEmailFrom")}</span></p>

                            <div className="space-y-2">
                              <Label className="text-sm font-medium">
                                <span><span key={tUi("nameExtractionRegex")}>{tUi("nameExtractionRegex")}</span></span>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <HelpCircle className="w-4 h-4 inline ml-1 text-gray-400 cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p><span key={tUi("regularExpressionToExtractNameFromEmailBody")}>{tUi("regularExpressionToExtractNameFromEmailBody")}</span></p>
                                  </TooltipContent>
                                </Tooltip>
                              </Label>
                              <Input
                                placeholder={"/Name:\\s*(.+?)(?:\\n|$)/mi"}
                                value={identityForm.name_regex}
                                onChange={(e) => setIdentityForm((prev) => ({ ...prev, name_regex: e.target.value }))}
                                className="text-sm font-mono"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label className="text-sm font-medium">
                                <span><span key={tUi("emailExtractionRegex")}>{tUi("emailExtractionRegex")}</span></span>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <HelpCircle className="w-4 h-4 inline ml-1 text-gray-400 cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p><span key={tUi("regularExpressionToExtractEmailAddressFromEmail")}>{tUi("regularExpressionToExtractEmailAddressFromEmail")}</span></p>
                                  </TooltipContent>
                                </Tooltip>
                              </Label>
                              <Input
                                placeholder={"/Email:\\s*([^\\s'<''>']+@[^\\s'<''>']+)/mi"}
                                value={identityForm.email_regex}
                                onChange={(e) => setIdentityForm((prev) => ({ ...prev, email_regex: e.target.value }))}
                                className="text-sm font-mono"
                              />
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>

                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div className="space-y-1">
                          <Label className="text-sm font-medium"><span key={tUi("enabled")}>{tUi("enabled")}</span></Label>
                          <p className="text-xs text-gray-500"><span key={tUi("enableThisIdentityOverrideRule")}>{tUi("enableThisIdentityOverrideRule")}</span></p>
                        </div>
                        <Switch
                          checked={identityForm.enabled}
                          onCheckedChange={(enabled) => setIdentityForm((prev) => ({ ...prev, enabled }))}
                        />
                      </div>

                      <div className="flex gap-3 pt-2">
                        <Button
                          onClick={handleAddOrUpdateIdentity}
                          disabled={!identityForm.name || !identityForm.source_email || !identityForm.email_regex}
                          className="flex-1"
                        >
                          <span key={editingIdentityIndex !== null ? tUi("update") : tUi("add")}>{editingIdentityIndex !== null ? tUi("update") : tUi("add")}</span> <span><span key={tUi("override")}>{tUi("override")}</span></span>
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleCancelIdentity}
                          className="flex-1 bg-transparent"
                        >
                          <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* How it works info */}
              <Collapsible open={infoExpanded} onOpenChange={setInfoExpanded}>
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    type="button"
                    className="flex items-center justify-between w-full p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                  >
                    <div className="flex items-center gap-2">
                      <Info className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800"><span key={tUi("howIdentityOverrideWorks")}>{tUi("howIdentityOverrideWorks")}</span></span>
                    </div>
                    {infoExpanded ? (
                      <ChevronUp className="w-4 h-4 text-blue-600" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-blue-600" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-3 bg-blue-50 border-l border-r border-b border-blue-200 rounded-b-lg -mt-1">
                    <div className="text-sm text-blue-800">
                      <div className="space-y-1 text-xs">
                        <p><span key={tUi("whenAnEmailArrivesFromAConfiguredSource")}>{tUi("whenAnEmailArrivesFromAConfiguredSource")}</span></p>
                        <p><span key={tUi("theSystemScansTheEmailBodyUsingYour")}>{tUi("theSystemScansTheEmailBodyUsingYour")}</span></p>
                        <p><span key={tUi("ifANameAndEmailAreFoundThey")}>{tUi("ifANameAndEmailAreFoundThey")}</span></p>
                        <p><span key={tUi("ifOnlyAnEmailIsFoundJustThe")}>{tUi("ifOnlyAnEmailIsFoundJustThe")}</span></p>
                        <p><span key={tUi("thisOverrideIdentityCanBeUsedForLead")}>{tUi("thisOverrideIdentityCanBeUsedForLead")}</span></p>
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Save button */}
              {hasUnsavedChanges && (
                <div className="flex gap-3 pt-2 border-t mt-4">
                  <Button
                    onClick={handleSaveIdentitySettings}
                    disabled={isSavingConfig}
                    className="flex-1"
                  >
                    {isSavingConfig ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        <span key={tCommonUi("saving")}>{tCommonUi("saving")}</span></>
                    ) : (
                      tUi("saveIdentitySettings")
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDiscardIdentityChanges}
                    disabled={isSavingConfig}
                    className="bg-transparent"
                  >
                    <span key={tUi("discard")}>{tUi("discard")}</span></Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
